import { apiRequest } from '../api/client';

export const PAYMENT_STATUSES = ['RECORDED', 'CLEARED', 'VOIDED'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** A catalog entry as the receipt shows it: the name frozen when the receipt was issued. */
export interface CatalogReference {
  id: string;
  name: string;
}

/** Matches `apps/api` `StoredWithholding` (`payments.repository.ts`). */
export interface PaymentWithholding {
  id: string;
  type: CatalogReference;
  amountCents: number;
  certificateNumber: string;
  jurisdiction: string | null;
}

/** Matches `apps/api` `StoredPayment` (`payments.repository.ts`). */
export interface Payment {
  id: string;
  receiptNumber: string;
  customerId: string;
  referenceNumber: string;
  paymentDate: string;
  amountReceivedCents: number;
  method: CatalogReference;
  notes: string | null;
  status: PaymentStatus;
  idempotencyKey: string;
  withholdings: PaymentWithholding[];
  createdAt: string;
}

/** Matches `apps/api` `StoredPaymentAllocation` (`payments.repository.ts`). */
export interface PaymentAllocation {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  amountCents: number;
  createdAt: string;
}

/** Matches `apps/api` `StoredPaymentDetail` (`payments.repository.ts`), the shape `GET /payments/:id` returns. */
export interface PaymentDetail extends Payment {
  allocations: PaymentAllocation[];
  unallocatedCents: number;
}

export interface PaymentSearchParams {
  customerId?: string | undefined;
  status?: PaymentStatus | undefined;
  fromDate?: string | undefined;
  toDate?: string | undefined;
  page: number;
  pageSize: number;
}

export interface PaymentSearchResult {
  items: Payment[];
  page: number;
  pageSize: number;
  total: number;
}

/** The largest page `GET /payments` accepts (`payment.dto.ts`'s `listPaymentsQuerySchema`). */
export const MAX_PAYMENT_PAGE_SIZE = 100;

/** Matches `apps/api` `GET /payments`. */
export function listPayments(params: PaymentSearchParams): Promise<PaymentSearchResult> {
  const query = new URLSearchParams();

  if (params.customerId !== undefined) query.set('customerId', params.customerId);
  if (params.status !== undefined) query.set('status', params.status);
  if (params.fromDate !== undefined) query.set('fromDate', params.fromDate);
  if (params.toDate !== undefined) query.set('toDate', params.toDate);
  query.set('page', String(params.page));
  query.set('pageSize', String(Math.min(params.pageSize, MAX_PAYMENT_PAGE_SIZE)));

  return apiRequest<PaymentSearchResult>(`/payments?${query.toString()}`);
}

/** Matches `apps/api` `GET /payments/:id`. */
export function getPayment(id: string): Promise<PaymentDetail> {
  return apiRequest<PaymentDetail>(`/payments/${id}`);
}

export interface WithholdingInput {
  typeId: string;
  amountCents: number;
  certificateNumber: string;
  jurisdiction?: string;
}

export interface RegisterPaymentInput {
  customerId: string;
  paymentDate: string;
  amountReceivedCents: number;
  methodId: string;
  referenceNumber: string;
  notes?: string;
  idempotencyKey: string;
  withholdings: WithholdingInput[];
}

/** Matches `apps/api` `POST /payments`. `200`, not `201`: idempotent replay returns the existing receipt. */
export function registerPayment(input: RegisterPaymentInput): Promise<Payment> {
  return apiRequest<Payment>('/payments', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Matches `apps/api` `AllocationSuggestionLine` (`domain/allocation-suggestion.ts`). */
export interface AllocationSuggestionLine {
  invoiceId: string;
  invoiceNumber: string;
  dueDate: string;
  amountCents: number;
}

export interface AllocationSuggestionResponse {
  suggestions: AllocationSuggestionLine[];
}

/** Matches `apps/api` `GET /payments/:id/allocation-suggestion`. A read-only FIFO proposal, never persisted. */
export function getAllocationSuggestion(id: string): Promise<AllocationSuggestionResponse> {
  return apiRequest<AllocationSuggestionResponse>(`/payments/${id}/allocation-suggestion`);
}

export interface AllocationLineInput {
  invoiceId: string;
  amountCents: number;
}

/** Matches `apps/api` `POST /payments/:id/allocations`. */
export function allocatePayment(
  id: string,
  allocations: AllocationLineInput[],
): Promise<PaymentDetail> {
  return apiRequest<PaymentDetail>(`/payments/${id}/allocations`, {
    method: 'POST',
    body: JSON.stringify({ allocations }),
  });
}

/** Matches `apps/api` `POST /payments/:id/void`. */
export function voidPayment(id: string, reason: string): Promise<PaymentDetail> {
  return apiRequest<PaymentDetail>(`/payments/${id}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}
